// src/App.js
import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';

import IntroScreen      from './components/IntroScreen';
import AuthPage         from './pages/AuthPage';
import DashboardPage    from './pages/DashboardPage';
import MissionsPage     from './pages/MissionsPage';
import EditorPage       from './pages/EditorPage';
import LeaderboardPage  from './pages/LeaderboardPage';
import ShopPage         from './pages/ShopPage';
import WorldPage        from './pages/WorldPage';
import ProfilePage      from './pages/ProfilePage';
import AchievementsPage from './pages/AchievementsPage';
import AdminDashboard   from './pages/AdminDashboard';
import Navbar           from './components/Navbar';
import Toast            from './components/Toast';
import CinematicEngine       from './components/CinematicEngine';
import GameAudioController   from './components/GameAudioController';

function Guard({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="loader" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function Layout({ children }) {
  const { user } = useAuth();
  return (
    <div className="app-layout">
      {user && <Navbar />}
      <main className="app-main">{children}</main>
      <Toast />
    </div>
  );
}

function InnerApp({ introDone }) {
  const { user } = useAuth();
  const [showIntro, setShowIntro] = useState(
    !user && !introDone && !sessionStorage.getItem('codrion_intro_done')
  );
  const handleIntroDone = useCallback(() => {
    sessionStorage.setItem('codrion_intro_done', '1');
    setShowIntro(false);
  }, []);

  if (showIntro) return <IntroScreen onComplete={handleIntroDone} />;

  return (
    <>
      <GameAudioController />
      <CinematicEngine />
      <Layout>
        <Routes>
          <Route path="/auth"          element={<AuthPage />} />
          <Route path="/"              element={<Guard><DashboardPage /></Guard>} />
          <Route path="/missions"      element={<Guard><MissionsPage /></Guard>} />
          <Route path="/missions/:id"  element={<Guard><EditorPage /></Guard>} />
          <Route path="/leaderboard"   element={<Guard><LeaderboardPage /></Guard>} />
          <Route path="/shop"          element={<Guard><ShopPage /></Guard>} />
          <Route path="/world"         element={<Guard><WorldPage /></Guard>} />
          <Route path="/profile"       element={<Guard><ProfilePage /></Guard>} />
          <Route path="/achievements"  element={<Guard><AchievementsPage /></Guard>} />
          <Route path="/admin"         element={<Guard adminOnly><AdminDashboard /></Guard>} />
          <Route path="*"              element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </>
  );
}

export default function App() {
  const introDone = !!sessionStorage.getItem('codrion_intro_done');
  return (
    <Router>
      <AuthProvider>
        <SocketProvider>
          <InnerApp introDone={introDone} />
        </SocketProvider>
      </AuthProvider>
    </Router>
  );
}
